import type { UsersFragment } from '@gql/graphql'

import { PRICING_PLAN_AMOUNTS, THIRTY_PERCENT_OFF_QUARTERLY } from '@canyon/constants/pricing-plans'
import { EmptySpace } from '@canyon/ui/EmptySpace'
import { cn } from '@canyon/ui/utils/cn'
import { Check, X } from '@phosphor-icons/react'

type TCheck = 'check' | 'cross'
type TSubscriptionPlanValue = React.ReactNode | TCheck | string
type TPricingPlans = 'Free' | 'Gold' | 'Pro'

interface IFeatureRow {
  planValues: {
    [key in TPricingPlans]: TSubscriptionPlanValue
  }
  title: string
  tooltip?: string
}

interface IFeatureObject {
  rows: IFeatureRow[]
  sectionTitle: string
  sectionValueHeader?: {
    [key in TPricingPlans]: React.ReactNode | string
  }
}

const isNewUser = (user: UsersFragment | null | undefined): boolean => {
  if (!user?.createdAt) return false
  const oneWeekAgo = new Date()
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)
  return new Date(user.createdAt) > oneWeekAgo
}

const getDisplayPrice = (
  planType: 'gold' | 'pro',
  selectedPricing: string,
  user: UsersFragment | null | undefined
): React.ReactNode => {
  const showNewUserDiscount = isNewUser(user) && selectedPricing === 'quarterly'

  if (!showNewUserDiscount) {
    const basePrice = PRICING_PLAN_AMOUNTS[planType][selectedPricing]
    return `$${basePrice}/month`
  }

  // Use the existing 30% off pricing from constants
  const discountedPrice = PRICING_PLAN_AMOUNTS[planType][THIRTY_PERCENT_OFF_QUARTERLY]
  const originalPrice = PRICING_PLAN_AMOUNTS[planType]['quarterly']

  return (
    <div className="flex flex-col">
      <div className="font-semibold">
        {discountedPrice ? (
          <>
            <span className="line-through text-muted-foreground">${originalPrice}</span> $
            {discountedPrice}/month
          </>
        ) : (
          `$${originalPrice}/month`
        )}
      </div>
    </div>
  )
}

const featuresMap = ({
  selectedPricing,
  user,
}: {
  selectedPricing: string
  user?: UsersFragment | null
}): IFeatureObject[] => [
  {
    rows: [
      {
        planValues: {
          Free: '$0/month',
          Gold: getDisplayPrice('gold', selectedPricing, user),
          Pro: getDisplayPrice('pro', selectedPricing, user),
        },
        title: 'Pricing',
      },
    ],
    sectionTitle: 'Plans',
    sectionValueHeader: {
      Free: 'Free',
      Gold: 'Gold',
      Pro: 'Pro',
    },
  },
  {
    rows: [
      {
        planValues: {
          Free: '3',
          Gold: 'Unlimited',
          Pro: 'Unlimited',
        },
        title: 'Resumes Limit',
      },
      {
        planValues: {
          Free: '1 Template',
          Gold: 'All Templates',
          Pro: 'All Templates',
        },
        title: 'Resume Templates',
      },
      {
        planValues: {
          Free: '4 Downloads',
          Gold: 'Unlimited',
          Pro: 'Unlimited',
        },
        title: 'Resume Downloads',
      },
      {
        planValues: {
          Free: '1 Token',
          Gold: 'Unlimited',
          Pro: 'Unlimited',
        },
        title: 'AI Resume Optimizations',
      },
      {
        planValues: {
          Free: '2 Tokens',
          Gold: 'Unlimited',
          Pro: 'Unlimited',
        },
        title: 'AI Summary Generations',
      },
      {
        planValues: {
          Free: '2 Tokens',
          Gold: 'Unlimited',
          Pro: 'Unlimited',
        },
        title: 'AI Achievement Generations',
      },
    ],
    sectionTitle: 'Resumes',
  },
  {
    rows: [
      {
        planValues: {
          Free: '2',
          Gold: 'Unlimited',
          Pro: 'Unlimited',
        },
        title: 'Cover Letter Generations',
      },
    ],
    sectionTitle: 'Cover Letters',
  },
  {
    rows: [
      {
        planValues: {
          Free: '100',
          Gold: 'Unlimited',
          Pro: 'Unlimited',
        },
        title: 'Tracked Applications',
      },
    ],
    sectionTitle: 'Job Applications',
  },
  {
    rows: [
      {
        planValues: {
          Free: '0 Tokens',
          Gold: 'Unlimited',
          Pro: 'Unlimited',
        },
        title: 'AI Answer Generations',
      },
      {
        planValues: {
          Free: 'Unlimited',
          Gold: 'Unlimited',
          Pro: 'Unlimited',
        },
        title: 'Application Autofill',
      },
      {
        planValues: {
          Free: '2 Tokens',
          Gold: 'Unlimited',
          Pro: 'Unlimited',
        },
        title: '"Am I a good fit for this job?" checks',
      },
    ],
    sectionTitle: 'Browser Extension',
  },
  {
    rows: [
      {
        planValues: {
          Free: '1 Token',
          Gold: 'Unlimited',
          Pro: 'Unlimited',
        },
        title: 'AI Mock Interviews',
      },
    ],
    sectionTitle: 'Interview Preparation',
  },
  {
    rows: [
      {
        planValues: {
          Free: '2 Tokens',
          Gold: 'Unlimited',
          Pro: 'Unlimited',
        },
        title: 'Learn Related Skills',
      },
    ],
    sectionTitle: 'Enrichment',
  },
  {
    rows: [
      {
        planValues: {
          Free: 'Up to 7 days',
          Gold: 'Up to 90 days',
          Pro: 'Up to 90 days',
        },
        title: 'Application Activity',
      },
    ],
    sectionTitle: 'Analytics',
  },
  {
    rows: [
      {
        planValues: {
          Free: '0',
          Gold: 'Unlimited',
          Pro: 'Unlimited',
        },
        title: 'AI Headshot Generation',
      },
    ],
    sectionTitle: 'AI Headshot Generation',
  },
  {
    rows: [
      {
        planValues: {
          Free: 'cross',
          Gold: 'check',
          Pro: 'cross',
        },
        title: 'Guided Application Tasks',
      },
      {
        planValues: {
          Free: 'cross',
          Gold: 'check',
          Pro: 'cross',
        },
        title: 'Company Enrichment',
      },
      {
        planValues: {
          Free: 'cross',
          Gold: 'check',
          Pro: 'cross',
        },
        title: 'Personalized Tone of Voice',
      },
      {
        planValues: {
          Free: 'cross',
          Gold: 'check',
          Pro: 'cross',
        },
        title: 'Contact Recommendations',
      },
      {
        planValues: {
          Free: 'cross',
          Gold: 'check',
          Pro: 'cross',
        },
        title: 'Tailored Networking Messages',
      },
    ],
    sectionTitle: 'Golden Route',
  },
]

interface IPricingTableProps {
  className?: string
  selectedPricing: string
  user?: UsersFragment | null
}

export const PricingTable = ({ className, selectedPricing, user }: IPricingTableProps) => {
  const features = featuresMap({ selectedPricing, user })

  return (
    <div
      className={cn(
        'border-border-muted flex grid auto-rows-max items-stretch rounded-lg border pb-4',
        className
      )}
    >
      {features.map((feature, index) => {
        const isFirstRow = index === 0
        const isLastRow = index === features.length - 1

        return (
          <div
            className={cn(
              'border-border-muted grid auto-cols-max grid-cols-4',
              !isLastRow && 'border-b'
            )}
          >
            <PricingHeaderColumn {...feature} />
            <PricingValueColumn
              headerValue={feature.sectionValueHeader?.['Free']}
              planValues={feature.rows.map((row) => row.planValues['Free'])}
              planValuesContrast={feature.sectionTitle === 'Plans'}
            />
            <PricingValueColumn
              headerValue={feature.sectionValueHeader?.['Pro']}
              planValues={feature.rows.map((row) => row.planValues['Pro'])}
              planValuesContrast={feature.sectionTitle === 'Plans'}
            />
            <PricingValueColumn
              className={`${isFirstRow ? 'rounded-tr-md' : ''} ${isLastRow ? 'rounded-br-md' : ''}`}
              headerValue={feature.sectionValueHeader?.['Gold']}
              planValues={feature.rows.map((row) => row.planValues['Gold'])}
              planValuesContrast={feature.sectionTitle === 'Plans'}
            />
          </div>
        )
      })}
    </div>
  )
}

interface IPricingColumnProps {
  children: React.ReactNode
  className?: string
}

const PricingColumn = ({ children, className }: IPricingColumnProps) => {
  return (
    <div className={cn('flex flex-col justify-between gap-3 px-3 py-4', className)}>{children}</div>
  )
}

interface IPricingHeaderColumnProps extends IFeatureObject {}
const PricingHeaderColumn = ({ rows, sectionTitle }: IPricingHeaderColumnProps) => {
  return (
    <PricingColumn>
      <div className="text-muted-foreground font-semibold">{sectionTitle}</div>
      <div className="flex flex-col">
        {rows.map((row) => (
          <div className="h-10">{row.title}</div>
        ))}
      </div>
    </PricingColumn>
  )
}

interface IPricingValueColumnProps {
  className?: string
  headerValue?: React.ReactNode | string
  planValues: TSubscriptionPlanValue[]
  planValuesContrast?: boolean
}

const PricingValueColumn = ({
  className,
  headerValue,
  planValues,
  planValuesContrast,
}: IPricingValueColumnProps) => {
  const renderValue = (value: TSubscriptionPlanValue) => {
    if (value === 'check') {
      return <Check />
    }

    if (value === 'cross') {
      return <X />
    }

    return value
  }

  return (
    <PricingColumn className={cn('border-border-muted border-l bg-stone-950/40', className)}>
      {headerValue ? <div>{headerValue}</div> : <EmptySpace />}
      <div className="flex flex-col">
        {planValues.map((value) => (
          <div
            className={cn(
              'text-muted-foreground h-10 text-sm',
              planValuesContrast && 'text-md font-semibold text-white'
            )}
          >
            {renderValue(value)}
          </div>
        ))}
      </div>
    </PricingColumn>
  )
}
